<script lang="ts" setup>
    import {onMounted, watch, ref, nextTick} from "vue";
    import DContainer from "../atoms/DContainer.vue";
    import DText from "../atoms/DText.vue";
    import DIcon from "../atoms/DIcon.vue";

    import {usePositionElement} from "../composables/usePositionElement";

    import type {ComponentThemes} from "@songfinch/types";

    withDefaults(defineProps<{
        info: string;
        theme?: ComponentThemes;
    }>(), {
        theme: "dark",
    });

    const dropdownWrapperRef = ref<{containerRef: HTMLElement} | null>(null);
    const dropdownRef = ref<{containerRef: HTMLElement} | null>(null);

    const {
        positionDropdown,
        arrowPosition
    } = usePositionElement();
    
    const isVisible = ref(false);

    const toggleDropdown = () => {
        isVisible.value = !isVisible.value;
    };

    const handleMouseLeave = () => {
        setTimeout(() => {
            isVisible.value = false;
        }, 300);
    };

    watch(() => isVisible.value, () => {
        if (isVisible.value) {
            nextTick(() => {
                positionDropdown(dropdownWrapperRef, dropdownRef);
            });
        }
    });

    onMounted(() => {
        positionDropdown(dropdownWrapperRef, dropdownRef);
    });
</script>

<template>
    <DContainer
        ref="dropdownRef"
        class="d-tooltip-container"
    >
        <DIcon
            size="small"
            name="info"
            :theme="theme"
            class="info-icon"
            @mouseenter="isVisible = true"
            @mouseleave="handleMouseLeave"
            @click="toggleDropdown" 
        />
        
        <transition name="info-fade">
            <DContainer 
                v-if="isVisible" 
                ref="dropdownRef"
                class="d-tooltip-dropdown"
                variant="card-tight"
                theme="white"
            >
                <DContainer :class="['d-tooltip-arrow', `d-tooltip-arrow--${arrowPosition}`]"/>
                <DText variant="p-5" color="muted">
                    {{info}}
                </DText>
            </DContainer>
        </transition>
    </DContainer>
</template>

<style lang="scss" scoped>
.d-tooltip-container {
    position: relative;
}

.d-tooltip-icon {
    cursor: pointer;
}

.d-tooltip-dropdown {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    max-width: 250px;
    width: max-content;
    z-index: 9;
    box-shadow: 0px 8px 16px 0px rgba(16, 16, 14, 0.30);
    white-space: normal;
    word-wrap: break-word;
    text-align: left;
}

/* Vue transition classes */
.d-tooltip-fade-enter-active, 
.d-tooltip-fade-leave-active {
    transition: opacity 0.2s ease-in-out, transform 0.2s ease-in-out;
    
}

.d-tooltip-fade-enter-from, 
.d-tooltip-fade-leave-to {
    transform: translateX(-50%) translateY(-5px);
    opacity: 0;
}

.d-tooltip-arrow {
  position: absolute;
  top: -6px;
  width: 12px;
  height: 12px;
  background: white;
  border-left: 1px solid var(--soft-black-tint-12);
  border-top: 1px solid var(--soft-black-tint-12);
  transform: rotate(45deg);
  z-index: -1;
}

.d-tooltip-arrow--center {
  left: 50%;
  transform: translateX(-50%) rotate(45deg);
}

.d-tooltip-arrow--left {
  left: 20px;
}

.d-tooltip-arrow--right {
  right: 20px;
}
</style>
